import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { Feature, FeatureCollection } from 'geojson';

import { useFloodAnalysisGridData } from '@/features/map/hooks/useFloodAnalysisGridData';
import { useHanoiFloodDemoGeoJson } from '@/features/map/hooks/useHanoiFloodDemoGeoJson';
import type { FloodCell } from '@/features/map/utils/demoDataGenerator';
import type { EnhancedFloodPoint } from '@/types/flood.types';

/* eslint-disable react-refresh/only-export-components -- Provider + hooks xuất cùng file */

export type FloodAnalysisGridContextValue = {
  floodGeoJson: FeatureCollection | null;
  floodPoints: EnhancedFloodPoint[];
  floodCells: FloodCell[];
};

const FloodAnalysisGridContext = createContext<FloodAnalysisGridContextValue | null>(null);

/**
 * Gom một lần GeoJSON vùng + lưới ô phân tích (cùng tham số map) cho map 2D/3D, sidebar và KPI.
 */
export function FloodAnalysisGridProvider({ children }: { children: ReactNode }) {
  const { floodGeoJson, floodPoints } = useHanoiFloodDemoGeoJson();
  const floodCells = useFloodAnalysisGridData(
    floodGeoJson?.features as readonly Feature[] | undefined,
  );

  const value = useMemo(
    (): FloodAnalysisGridContextValue => ({
      floodGeoJson,
      floodPoints,
      floodCells,
    }),
    [floodGeoJson, floodPoints, floodCells],
  );

  return (
    <FloodAnalysisGridContext.Provider value={value}>{children}</FloodAnalysisGridContext.Provider>
  );
}

export function useFloodAnalysisGrid(): FloodAnalysisGridContextValue {
  const ctx = useContext(FloodAnalysisGridContext);
  if (!ctx) {
    throw new Error('useFloodAnalysisGrid must be used within FloodAnalysisGridProvider');
  }
  return ctx;
}

/** Trả về `null` ngoài provider — dùng khi hook cần fallback không có lưới phân tích. */
export function useOptionalFloodAnalysisGrid(): FloodAnalysisGridContextValue | null {
  return useContext(FloodAnalysisGridContext);
}
